import type { Metadata } from "next";

export {
  BRAND_HOME_ARIA_LABEL,
  BRAND_LOGO_ALT,
  BRAND_LOGO_TITLE,
} from "@v1/ui/brand-logo-mark";

/** Product brand string used in titles and image alt text. */
export const BRAND_NAME: string = "hausverwalter.ai";

/** Absolute site origin — same source and fallback as the root `metadataBase`. */
export const SITE_URL: string =
  process.env.NEXT_PUBLIC_SITE_URL ?? "https://app.hausverwalter.ai";

/** Primary SEO description — natural keyword coverage, no stuffing. */
export const SITE_DESCRIPTION: string =
  "Hausverwaltung Software für Vorgänge, Buchhaltung, Kundenportal und DMS. Online-Eigentümerversammlung, Telefonanlage und KI-Agenten — Alternative zu Casavi, iDWELL, facilioo und etg24.";

/**
 * Category term the landing page targets in search. It appears in metadata,
 * alt text and accessible names only — never as visible UI copy.
 */
export const AI_CATEGORY_TERM: string = "KI für Hausverwaltungen";

/** Alt text for the logo lockups on the landing page. */
export const MARKETING_LOGO_ALT: string = `hausverwalter.ai — ${AI_CATEGORY_TERM}`;

/**
 * Accessible name of the interactive product window on the landing page. The
 * window is a live mock of the app, so screen readers (and crawlers) get the
 * description a screenshot would otherwise carry in its alt text.
 */
export const MARKETING_PRODUCT_WINDOW_LABEL: string = `hausverwalter.ai in Aktion — ${AI_CATEGORY_TERM}: Inbox, Dokumente, Telefonanlage, Kundenportal und KI-Agenten in einer Oberfläche`;

export const MEET_DANIEL_ALT: string =
  "Online hybride Eigentümerversammlung: Hausverwalter führt die ETV in hausverwalter.ai";

export const MEET_DANIEL_TITLE: string =
  "Online-Eigentümerversammlung für Hausverwaltungen — hausverwalter.ai";

export const MEET_WOLFGANG_ALT: string =
  "Eigentümer im Kundenportal bei der Online-Eigentümerversammlung über hausverwalter.ai";

export const MEET_WOLFGANG_TITLE: string =
  "ETV Software Hausverwaltung — Kundenportal";

export const OPEN_GRAPH_IMAGE_PATH: string = "/brand/logo-light.png";

/** Shared Open Graph + Twitter card defaults for marketing pages. */
export function buildMarketingOpenGraph(
  title: string,
  description: string,
): Metadata["openGraph"] {
  return {
    title,
    description,
    siteName: BRAND_NAME,
    locale: "de_DE",
    type: "website",
    images: [
      {
        url: OPEN_GRAPH_IMAGE_PATH,
        alt: MARKETING_LOGO_ALT,
      },
    ],
  };
}

export function buildMarketingTwitter(
  title: string,
  description: string,
): Metadata["twitter"] {
  return {
    card: "summary",
    title,
    description,
    images: [OPEN_GRAPH_IMAGE_PATH],
  };
}

/** Base metadata fields reused across public marketing routes. */
export function buildMarketingMetadata(
  title: string,
  description: string = SITE_DESCRIPTION,
): Metadata {
  return {
    title,
    description,
    openGraph: buildMarketingOpenGraph(title, description),
    twitter: buildMarketingTwitter(title, description),
  };
}

/**
 * German marketing default for {@link orgLogoTitle}. Booking and brand pages
 * keep this suffix unless a caller passes a translated `portalProductName`.
 */
// i18n-exempt: German marketing SEO default for booking/brand pages; portal callers pass a translated suffix.
export const DEFAULT_PORTAL_PRODUCT_NAME: string =
  "Kundenportal Hausverwaltung";

/** Alt text for a tenant / customer org logo on public surfaces. */
export function orgLogoAlt(orgName: string): string {
  return `Logo ${orgName}`;
}

/** Title for a tenant org logo on booking / portal pages. */
export function orgLogoTitle(
  orgName: string,
  portalProductName: string = DEFAULT_PORTAL_PRODUCT_NAME,
): string {
  return `${orgName} — ${portalProductName}`;
}

/** Alt text for a publicly shared file image preview. */
export function sharedFilePreviewAlt(fileName: string): string {
  return `Vorschau der geteilten Datei ${fileName}`;
}

/** Title for a publicly shared file image preview. */
export function sharedFilePreviewTitle(fileName: string): string {
  return `Geteilte Datei ${fileName} — hausverwalter.ai DMS`;
}
